import { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  doc,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useAuth } from "../../context/AuthContext";

/**
 * Props:
 * - founderId (required)
 * - mode: "review" | "view"
 * - onEvaluationLoaded?: function({ evaluationId, status })
 */
function EvaluationPanel({ founderId, mode, onEvaluationLoaded }) {
  const { user } = useAuth();

  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [evaluationId, setEvaluationId] = useState(null);
  const [status, setStatus] = useState("draft");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isReviewMode = mode === "review";
  const isLocked = status === "submitted";

  // 🔒 VERY IMPORTANT: prevents re-loading & overwriting user input
  const hasLoadedOnce = useRef(false);

  /* =========================
     LOAD EXISTING EVALUATION
     (RUNS ONLY ONCE)
  ========================= */
  useEffect(() => {
    if (!user || !founderId) {
      setError("Invalid startup reference.");
      setLoading(false);
      return;
    }

    if (hasLoadedOnce.current) return;
    hasLoadedOnce.current = true;

    const loadEvaluation = async () => {
      try {
        const q = query(
          collection(db, "evaluations"),
          where("evaluatorId", "==", user.uid),
          where("founderId", "==", founderId),
          limit(2)
        );

        const snap = await getDocs(q);

        if (!snap.empty) {
          const d = snap.docs[0];
          const data = d.data();

          setEvaluationId(d.id);
          setRating(data.rating || 0);
          setNotes(data.notes || "");
          setStatus(data.status || "draft");

          onEvaluationLoaded?.({
            evaluationId: d.id,
            status: data.status || "draft",
          });
        } else {
          onEvaluationLoaded?.({
            evaluationId: null,
            status: "draft",
          });
        }
      } catch (err) {
        console.error("Failed to load evaluation", err);
        setError("Failed to load evaluation.");
      } finally {
        setLoading(false);
      }
    };

    loadEvaluation();
  }, [user, founderId, onEvaluationLoaded]);

  /* =========================
     SAVE DRAFT
  ========================= */
  const saveDraft = async () => {
    if (saving) return;

    if (rating < 1 || rating > 5) {
      alert("Please select a rating between 1 and 5.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      setError("");

      let evalId = evaluationId;

      if (evaluationId) {
        await updateDoc(doc(db, "evaluations", evaluationId), {
          rating,
          notes,
          status: "draft",
          updatedAt: serverTimestamp(),
        });
      } else {
        const ref = await addDoc(collection(db, "evaluations"), {
          evaluatorId: user.uid,
          founderId,
          rating,
          notes,
          status: "draft",
          claimRatings: {},
          evidenceRatings: {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        evalId = ref.id;
        setEvaluationId(ref.id);
      }

      setStatus("draft");

      onEvaluationLoaded?.({
        evaluationId: evalId,
        status: "draft",
      });

      setMessage(
        "Draft saved. This evaluation is private and not visible to the founder."
      );
    } catch (err) {
      console.error(err);
      alert("Failed to save draft.");
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     SUBMIT FINAL EVALUATION
  ========================= */
  const submitEvaluation = async () => {
    if (saving) return;

    if (rating < 1 || rating > 5) {
      alert("Please select a rating between 1 and 5.");
      return;
    }

    const ok = window.confirm(
      "Submit this evaluation?\n\n• The founder WILL NOT be able to see it\n• You will NOT be able to edit it later\n• This is advisory feedback, not certification"
    );
    if (!ok) return;

    try {
      setSaving(true);
      setMessage("");
      setError("");

      let evalId = evaluationId;

      // ✅ Ensure evaluation exists and rating/notes are persisted
      if (evaluationId) {
        await updateDoc(doc(db, "evaluations", evaluationId), {
          rating,
          notes,
          status: "submitted",
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } else {
        const ref = await addDoc(collection(db, "evaluations"), {
          evaluatorId: user.uid,
          founderId,
          rating,
          notes,
          status: "submitted",
          claimRatings: {},
          evidenceRatings: {},
          createdAt: serverTimestamp(),
          submittedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        evalId = ref.id;
        setEvaluationId(ref.id);
      }

      setStatus("submitted");

      onEvaluationLoaded?.({
        evaluationId: evalId,
        status: "submitted",
      });

      setMessage(
        "Evaluation submitted. The founder can now view descriptive anonymous feedback."
      );
    } catch (err) {
      console.error(err);
      alert("Failed to submit evaluation.");
    } finally {
      setSaving(false);
    }
  };

  /* =========================
     RENDER
  ========================= */
  if (loading) return <p>Loading evaluation…</p>;

  if (error) {
    return (
      <div className="card" style={{ padding: 24, marginBottom: 32 }}>
        <p style={{ color: "#dc2626" }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 24, marginBottom: 32 }}>
      <h3>My Evaluation</h3>

      <p style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}>
        Status:{" "}
        <strong>{status === "submitted" ? "Submitted" : "Draft"}</strong>
      </p>

      {status === "draft" && isReviewMode && (
        <div style={{ fontSize: 13, color: "#475569", marginBottom: 16 }}>
          💡 You can freely edit this draft. The founder never sees your notes or ratings.
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>Overall Rating</label>
        <div style={{ marginTop: 8 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              disabled={!isReviewMode || isLocked || saving}
              className={rating === n ? "btn-primary" : "btn-secondary"}
              style={{ marginRight: 8 }}
            >
              {n} ⭐
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontWeight: 600 }}>Notes</label>
        <textarea
          value={notes}
          disabled={!isReviewMode || isLocked || saving}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          style={{
            width: "100%",
            marginTop: 8,
            padding: 10,
            borderRadius: 6,
            border: "1px solid #e5e7eb",
          }}
        />
      </div>

      {isReviewMode && !isLocked && (
        <div style={{ display: "flex", gap: 12 }}>
          <button className="btn-secondary" onClick={saveDraft} disabled={saving}>
            {saving ? "Saving…" : "Save Draft"}
          </button>

          <button className="btn-primary" onClick={submitEvaluation} disabled={saving}>
            {saving ? "Submitting…" : "Submit Evaluation"}
          </button>
        </div>
      )}

      {isLocked && (
        <p style={{ marginTop: 12, color: "#2563eb", fontSize: 14 }}>
          This evaluation has been submitted and is now read-only.
        </p>
      )}

      {message && (
        <p style={{ marginTop: 12, color: "#16a34a", fontSize: 14 }}>
          {message}
        </p>
      )}
    </div>
  );
}

export default EvaluationPanel;
